import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, ImagePlus, Loader2, MapPin, Minus, Plus, Truck, X } from "lucide-react";
import { COLORS, GARMENTS, SHIPPING, SIZES, priceLine, unitPrice } from "@/lib/pricing";
import OrderSummary from "@/components/OrderSummary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-sm border border-[#E5D8D3] bg-[#FDFBF7] px-4 py-3 text-sm text-[#1A2421] outline-none transition-colors placeholder:text-[#B9AEA4] focus:border-[#D4AF37]";

const StepTitle = ({ n, children }) => (
  <div className="flex items-baseline gap-3">
    <span className="ff-script text-2xl leading-none text-[#B08A2E]">{n}</span>
    <h3 className="text-[11px] uppercase tracking-[0.28em] text-[#1A2421]/55">{children}</h3>
  </div>
);

export default function OrderSection({ garment, setGarment }) {
  const [size, setSize] = useState("M");
  const [colorName, setColorName] = useState("White");
  const [customColor, setCustomColor] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [delivery, setDelivery] = useState("ship");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", street: "", city: "", state: "", zip: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const finalColor = colorName === "Custom" ? customColor.trim() : colorName;
  const swatch = COLORS.find((c) => c.name === colorName) || { name: finalColor || "Custom", hex: "#D87D92" };
  const unit = unitPrice(garment, size);
  const subtotal = unit * quantity;
  const shippingCost = delivery === "pickup" ? 0 : SHIPPING;
  const total = subtotal + shippingCost;

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickFile = (f) => {
    if (!f) return;
    if (!/\.(png|jpe?g)$/i.test(f.name)) {
      toast.error("Please choose a PNG or JPG image for your design");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Design file must be under 10MB");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const reset = () => {
    setSize("M");
    setColorName("White");
    setCustomColor("");
    setQuantity(1);
    setNotes("");
    setDelivery("ship");
    setFile(null);
    setPreview(null);
    setForm({ name: "", email: "", phone: "", street: "", city: "", state: "", zip: "" });
    setResult(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please add your name");
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return toast.error("Please add a valid email");
    if (!finalColor) return toast.error("Please tell us your shirt color");
    if (delivery === "ship" && (!form.street.trim() || !form.city.trim() || !form.state.trim() || !form.zip.trim()))
      return toast.error("Please complete your shipping address");

    const fd = new FormData();
    fd.append("name", form.name.trim());
    fd.append("email", form.email.trim());
    fd.append("phone", form.phone.trim());
    fd.append("garment", garment);
    fd.append("size", size);
    fd.append("color", finalColor);
    fd.append("quantity", String(quantity));
    fd.append("design_notes", notes.trim());
    fd.append("delivery_method", delivery === "pickup" ? "pickup" : "shipping");
    fd.append("street", form.street.trim());
    fd.append("city", form.city.trim());
    fd.append("state", form.state.trim());
    fd.append("zip_code", form.zip.trim());
    if (file) fd.append("design_file", file);

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/orders`, fd);
      setResult(data);
      toast.success(`Order ${data.order_number} received`);
      window.__lenis?.scrollTo("#order", { offset: -72 });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not place your order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="order" className="relative bg-[#F4EAE6] py-24 lg:py-32" data-testid="order-section">
      <div
        aria-hidden="true"
        className="absolute -left-32 top-24 h-96 w-96 rounded-full bg-gradient-to-br from-[#D87D92] to-[#D4AF37] opacity-15 blur-3xl"
      />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mb-14"
        >
          <div className="mb-4 flex items-center gap-4">
            <span className="h-px w-12 bg-[#D4AF37]" />
            <span className="text-[11px] uppercase tracking-[0.34em] text-[#2A5C43]">Place Your Order</span>
          </div>
          <h2 className="ff-serif text-4xl font-light tracking-tight sm:text-5xl">
            Design it. <span className="ff-script text-brand-gradient text-5xl font-semibold sm:text-6xl">We craft it.</span>
          </h2>
        </motion.div>

        <div className="grid items-start gap-10 lg:grid-cols-[1fr_400px]">
          <AnimatePresence mode="wait">
            {result ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="rounded-sm border border-[#E5D8D3] bg-white p-10 shadow-[0_8px_30px_rgba(212,175,55,0.08)] sm:p-14"
                data-testid="order-success"
              >
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#2A5C43] to-[#D87D92]">
                  <Check size={28} className="text-[#FDFBF7]" />
                </div>
                <h3 className="ff-serif mt-8 text-4xl font-light">Your dream is in the queue</h3>
                <p className="mt-3 max-w-md text-sm font-light leading-relaxed text-[#1A2421]/65">
                  Order <span className="font-medium tracking-[0.18em] text-[#B08A2E]" data-testid="success-order-number">{result.order_number}</span> has
                  been sent to the atelier. Makie will email you shortly to confirm your design and
                  arrange payment of <span className="font-medium text-[#1A2421]">${result.total}</span>{" "}
                  {result.delivery_method === "pickup"
                    ? "— local pickup is free, and pickup details will be confirmed by email."
                    : "(including $10 shipping)."}
                </p>
                <div className="mt-8 flex flex-wrap gap-3 text-xs uppercase tracking-[0.2em] text-[#1A2421]/60">
                  <span className="border border-[#E5D8D3] px-4 py-2">{result.garment_name}</span>
                  <span className="border border-[#E5D8D3] px-4 py-2">Size {result.size}</span>
                  <span className="border border-[#E5D8D3] px-4 py-2">{result.color}</span>
                  <span className="border border-[#E5D8D3] px-4 py-2">Qty {result.quantity}</span>
                  <span className="border border-[#E5D8D3] px-4 py-2" data-testid="success-delivery-chip">
                    {result.delivery_method === "pickup" ? "Local pickup" : "Shipping"}
                  </span>
                </div>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={reset}
                  className="mt-10 rounded-full bg-[#1A2421] px-8 py-4 text-xs uppercase tracking-[0.24em] text-[#FDFBF7] transition-colors hover:bg-[#2A5C43]"
                  data-testid="place-another-order-btn"
                >
                  Place Another Order
                </motion.button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={submit}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="space-y-12 rounded-sm border border-[#E5D8D3] bg-white p-7 shadow-[0_8px_30px_rgba(212,175,55,0.08)] sm:p-12"
                data-testid="order-form"
              >
                <div>
                  <StepTitle n="01">Choose your piece</StepTitle>
                  <div className="mt-5 grid gap-3 sm:grid-cols-3">
                    {Object.entries(GARMENTS).map(([key, g]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setGarment(key)}
                        className={`rounded-sm border p-4 text-left transition-colors ${
                          garment === key
                            ? "border-[#D4AF37] bg-[#FDFBF7] shadow-[0_8px_30px_rgba(212,175,55,0.15)]"
                            : "border-[#E5D8D3] hover:border-[#D4AF37]/60"
                        }`}
                        data-testid={`garment-option-${key}`}
                      >
                        <span className="ff-serif block text-lg leading-tight">{g.name}</span>
                        <span className="mt-1 block text-[10px] uppercase tracking-[0.2em] text-[#B08A2E]">
                          {priceLine(key)}
                        </span>
                      </button>
                    ))}
                  </div>
                  <div className="mt-5 flex items-center gap-4">
                    <span className="text-[11px] uppercase tracking-[0.22em] text-[#1A2421]/55">Quantity</span>
                    <div className="flex items-center rounded-full border border-[#E5D8D3]">
                      <button type="button" onClick={() => setQuantity((q) => Math.max(1, q - 1))} className="p-2.5 text-[#1A2421]/60 transition-colors hover:text-[#B08A2E]" data-testid="quantity-minus-btn" aria-label="Decrease quantity">
                        <Minus size={14} />
                      </button>
                      <span className="w-8 text-center text-sm" data-testid="quantity-value">{quantity}</span>
                      <button type="button" onClick={() => setQuantity((q) => Math.min(50, q + 1))} className="p-2.5 text-[#1A2421]/60 transition-colors hover:text-[#B08A2E]" data-testid="quantity-plus-btn" aria-label="Increase quantity">
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <StepTitle n="02">Pick a size</StepTitle>
                  <div className="mt-5 grid grid-cols-4 gap-2 sm:grid-cols-8">
                    {SIZES.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setSize(s)}
                        className={`border py-2.5 text-xs tracking-[0.12em] transition-colors ${
                          size === s
                            ? "border-[#1A2421] bg-[#1A2421] text-[#FDFBF7]"
                            : "border-[#E5D8D3] text-[#1A2421]/70 hover:border-[#D4AF37]"
                        }`}
                        data-testid={`size-option-${s}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  <p className="mt-3 text-xs font-light text-[#1A2421]/50">
                    Sizes 2X and above are priced as shown on each piece.
                  </p>
                </div>

                <div>
                  <StepTitle n="03">Shirt color</StepTitle>
                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    {COLORS.map((c) => (
                      <button
                        key={c.name}
                        type="button"
                        onClick={() => setColorName(c.name)}
                        title={c.name}
                        className={`h-9 w-9 rounded-full border transition-transform hover:scale-110 ${
                          colorName === c.name ? "ring-2 ring-[#D4AF37] ring-offset-2 ring-offset-white" : "border-[#E5D8D3]"
                        }`}
                        style={{ background: c.hex }}
                        data-testid={`color-swatch-${c.name.toLowerCase()}`}
                        aria-label={`Color ${c.name}`}
                      />
                    ))}
                    <button
                      type="button"
                      onClick={() => setColorName("Custom")}
                      className={`rounded-full border px-4 py-2 text-[10px] uppercase tracking-[0.18em] transition-colors ${
                        colorName === "Custom" ? "border-[#D4AF37] bg-[#FDFBF7] text-[#B08A2E]" : "border-[#E5D8D3] text-[#1A2421]/60 hover:border-[#D4AF37]"
                      }`}
                      data-testid="color-swatch-custom"
                    >
                      Custom
                    </button>
                  </div>
                  {colorName === "Custom" && (
                    <input
                      value={customColor}
                      onChange={(e) => setCustomColor(e.target.value)}
                      placeholder="Tell us your dream color…"
                      className={`${inputCls} mt-4 max-w-sm`}
                      data-testid="custom-color-input"
                    />
                  )}
                </div>

                <div>
                  <StepTitle n="04">Your design</StepTitle>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    placeholder="Describe your design — text, placement, style, inspiration…"
                    className={`${inputCls} mt-5 resize-none`}
                    data-testid="design-notes-input"
                  />
                  <div className="mt-4">
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".png,.jpg,.jpeg"
                      className="hidden"
                      onChange={(e) => pickFile(e.target.files?.[0])}
                      data-testid="design-file-input"
                    />
                    {preview ? (
                      <div className="flex items-center gap-4 rounded-sm border border-[#E5D8D3] bg-[#FDFBF7] p-4" data-testid="design-preview">
                        <img src={preview} alt="Your design preview" className="h-20 w-20 rounded-sm object-cover" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm">{file?.name}</p>
                          <p className="text-xs text-[#1A2421]/50">Ready to attach to your order</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setFile(null);
                            setPreview(null);
                            if (fileRef.current) fileRef.current.value = "";
                          }}
                          className="p-2 text-[#1A2421]/50 transition-colors hover:text-[#D87D92]"
                          data-testid="remove-design-btn"
                          aria-label="Remove design file"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        className="flex w-full items-center justify-center gap-3 rounded-sm border border-dashed border-[#D4AF37]/60 bg-[#FDFBF7] px-6 py-8 text-sm text-[#1A2421]/60 transition-colors hover:border-[#D4AF37] hover:text-[#B08A2E]"
                        data-testid="design-upload-dropzone"
                      >
                        <ImagePlus size={18} />
                        Upload your design (PNG or JPG)
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <StepTitle n="05">Delivery &amp; contact</StepTitle>
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => setDelivery("ship")}
                      className={`flex items-center gap-4 rounded-sm border p-4 text-left transition-colors ${
                        delivery === "ship"
                          ? "border-[#D4AF37] bg-[#FDFBF7] shadow-[0_8px_30px_rgba(212,175,55,0.15)]"
                          : "border-[#E5D8D3] hover:border-[#D4AF37]/60"
                      }`}
                      data-testid="delivery-ship-option"
                    >
                      <Truck size={20} className="shrink-0 text-[#B08A2E]" />
                      <span>
                        <span className="ff-serif block text-lg leading-tight">Ship it to me</span>
                        <span className="mt-0.5 block text-[10px] uppercase tracking-[0.2em] text-[#B08A2E]">
                          $10 flat · to your door
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setDelivery("pickup")}
                      className={`flex items-center gap-4 rounded-sm border p-4 text-left transition-colors ${
                        delivery === "pickup"
                          ? "border-[#D4AF37] bg-[#FDFBF7] shadow-[0_8px_30px_rgba(212,175,55,0.15)]"
                          : "border-[#E5D8D3] hover:border-[#D4AF37]/60"
                      }`}
                      data-testid="delivery-pickup-option"
                    >
                      <MapPin size={20} className="shrink-0 text-[#2A5C43]" />
                      <span>
                        <span className="ff-serif block text-lg leading-tight">Local pickup</span>
                        <span className="mt-0.5 block text-[10px] uppercase tracking-[0.2em] text-[#2A5C43]">
                          Free · save the $10
                        </span>
                      </span>
                    </button>
                  </div>

                  <div className="mt-5 grid gap-4 sm:grid-cols-2">
                    <input value={form.name} onChange={setField("name")} placeholder="Full name *" className={inputCls} data-testid="input-name" />
                    <input value={form.email} onChange={setField("email")} type="email" placeholder="Email *" className={inputCls} data-testid="input-email" />
                    <input value={form.phone} onChange={setField("phone")} placeholder="Phone (optional)" className={inputCls} data-testid="input-phone" />
                  </div>

                  {delivery === "ship" ? (
                    <div className="mt-4 grid gap-4 sm:grid-cols-2" data-testid="shipping-address-fields">
                      <input value={form.street} onChange={setField("street")} placeholder="Street address *" className={inputCls} data-testid="input-street" />
                      <input value={form.city} onChange={setField("city")} placeholder="City *" className={inputCls} data-testid="input-city" />
                      <div className="grid grid-cols-2 gap-4 sm:col-span-2 sm:max-w-[50%]">
                        <input value={form.state} onChange={setField("state")} placeholder="State *" className={inputCls} data-testid="input-state" />
                        <input value={form.zip} onChange={setField("zip")} placeholder="ZIP *" className={inputCls} data-testid="input-zip" />
                      </div>
                    </div>
                  ) : (
                    <p className="mt-4 text-xs font-light leading-relaxed text-[#1A2421]/55" data-testid="pickup-note">
                      No address needed — Makie will email you to arrange a pickup time and place.
                    </p>
                  )}
                </div>

                <motion.button
                  type="submit"
                  disabled={submitting}
                  whileHover={{ scale: submitting ? 1 : 1.01 }}
                  whileTap={{ scale: submitting ? 1 : 0.98 }}
                  className="flex w-full items-center justify-center gap-3 rounded-full bg-gradient-to-r from-[#2A5C43] via-[#7A8B5C] to-[#D87D92] px-8 py-4 text-xs uppercase tracking-[0.28em] text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                  data-testid="submit-order-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={15} className="animate-spin" /> Sending to the atelier…
                    </>
                  ) : (
                    <>Place Order — ${total}</>
                  )}
                </motion.button>
              </motion.form>
            )}
          </AnimatePresence>

          <OrderSummary
            garment={garment}
            size={size}
            color={swatch}
            quantity={quantity}
            unit={unit}
            subtotal={subtotal}
            shipping={shippingCost}
            delivery={delivery}
            total={total}
          />
        </div>
      </div>
    </section>
  );
}
